export type AppEnv = "staging" | "uat" | "production";

const raw = (import.meta.env.VITE_APP_ENV ?? "staging").toString().toLowerCase();
export const APP_ENV: AppEnv =
  raw === "production" || raw === "uat" || raw === "staging"
    ? (raw as AppEnv)
    : "staging";

const rawBase = (import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? "")
  .toString()
  .replace(/\/$/, "");

if (!rawBase) {
  const msg =
    "[DigaVouch] VITE_API_BASE_URL is not set. " +
    "Set it to the backend base URL (e.g. https://api.staging.digavouch.com) " +
    "in your hosting provider's environment variables and redeploy.";
  if (import.meta.env.PROD) {
    console.error(msg);
    throw new Error(msg);
  } else {
    console.warn(msg);
  }
}

export const API_BASE_URL: string = rawBase;

// Google OAuth Web client ID for the admin surface (admin.digavouch.com
// /tenants). DEDICATED client, separate from any flow OAuth. The admin page's
// "Sign in with Google" uses this; the backend verifies the token's aud == this.
export const ADMIN_GOOGLE_CLIENT_ID: string = (import.meta.env.VITE_ADMIN_GOOGLE_CLIENT_ID ?? "")
  .toString()
  .trim();

export const IS_STAGING = APP_ENV === "staging";
export const IS_UAT = APP_ENV === "uat";
export const IS_PRODUCTION = APP_ENV === "production";

// Whether this BUILD carries the demo review surfaces (the /article and
// /feather routes, their pages, fixtures and partner branding). A literal
// comparison on purpose: Vite replaces `import.meta.env.VITE_APP_ENV` with a
// string at build time and the bundler folds the comparison, so a production
// build drops every module only reachable behind it. `IS_PRODUCTION` cannot do
// that (it is computed at runtime). Read both: the build constant removes the
// code, `IS_PRODUCTION` keeps the routes not-found in any build.
export const DEMO_SURFACES_IN_BUILD = import.meta.env.VITE_APP_ENV !== "production";

// Plaid payroll-income section on the applicant flow (step 3). Build-time flag,
// paired with the backend's PLAID_INCOME_ENABLED (Terraform). Set
// VITE_SHOW_FINANCIAL_ACCOUNTS="true"/"false" per Netlify site to control it
// without a code change. When unset it defaults to the historical behavior:
// on in staging/UAT, off in production. Flip the prod site's var to "true"
// (and plaid_income_enabled_prod=true) to launch in production.
const rawShowFinancial = (import.meta.env.VITE_SHOW_FINANCIAL_ACCOUNTS ?? "")
  .toString()
  .trim()
  .toLowerCase();
export const SHOW_FINANCIAL_ACCOUNTS =
  rawShowFinancial === "true"
    ? true
    : rawShowFinancial === "false"
      ? false
      : !IS_PRODUCTION;

// Pay-stub UPLOAD fallback on the payroll card (Plaid Document Income).
// Build-time flag paired with the backend's PLAID_DOCUMENT_INCOME_ENABLED
// (Terraform) — both must be on for the fallback to work. Set
// VITE_SHOW_PAYROLL_DOC_FALLBACK="true"/"false" per Netlify site.
//
// When unset it FOLLOWS SHOW_FINANCIAL_ACCOUNTS: the upload fallback is
// meaningless without the payroll card it lives on, so it can never be on
// while that card is hidden. That also keeps it off in production by default,
// where Document Income needs separate Plaid production access.
//
// Uploaded pay stubs are a WEAKER signal than a connected payroll account
// (0.70 vs 0.95) — the UI must keep upload subordinate to connecting. See
// digavouch-platform/docs/design/plaid-document-income-fallback.md.
const rawShowDocFallback = (import.meta.env.VITE_SHOW_PAYROLL_DOC_FALLBACK ?? "")
  .toString()
  .trim()
  .toLowerCase();
export const SHOW_PAYROLL_DOC_FALLBACK =
  SHOW_FINANCIAL_ACCOUNTS &&
  (rawShowDocFallback === "true"
    ? true
    : rawShowDocFallback === "false"
      ? false
      : !IS_PRODUCTION);

// Partner / tenant branding is now resolved at RUNTIME from the host
// via `useTenant()` (see src/hooks/useTenant.ts). The legacy
// VITE_PARTNER_NAME / VITE_PARTNER_LOGO build-time vars have been
// retired — do not re-add them.

if (typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.info(`[DigaVouch] env=${APP_ENV} api=${API_BASE_URL || "(unset)"}`);
}
